import type {
  CallMessage,
  CallMessageTalker,
  ChatMessageSpeakerDto,
  TranscriptSegmentDisplay,
  TranscriptSegmentNote,
  TranscriptSpeakerType,
} from '@/types/call';

const CHAT_DEBUG_PREFIX = '[chatMessages]';
const CHAT_DEBUG_STORAGE_KEY = 'spog.chatMessages.debug';

function summarizeChatMessage(message: CallMessage) {
  return {
    talker: message.talker,
    messageId: message.messageId,
    timestamp: message.timestamp,
    text: normalizeMessageText(message.message),
    speakerObject: message.speakerObject,
  };
}

function debugLog(label: string, payload?: unknown): void {
  if (
    typeof window === 'undefined' ||
    window.localStorage.getItem(CHAT_DEBUG_STORAGE_KEY) !== 'true'
  ) {
    return;
  }
  console.log(`${CHAT_DEBUG_PREFIX} ${label}`, payload);
}

/** Stable key for deduping / matching chat rows (init vs Connect). */
export function chatMessageKey(m: CallMessage): string {
  const key = `${m.talker ?? ''}|${m.message ?? ''}|${m.timestamp ?? ''}`;
  debugLog('chatMessageKey', { key, message: summarizeChatMessage(m) });
  return key;
}

function normalizeMessageText(text?: string): string {
  const normalized = (text ?? '').trim().replace(/\s+/g, ' ');
  debugLog('normalizeMessageText', { raw: text, normalized });
  return normalized;
}

export function normalizeChatTalkerFromApi(
  talker?: string
): CallMessageTalker | undefined {
  const t = (talker ?? '').toLowerCase();
  if (t === 'customer' || t === 'client') {
    debugLog('normalizeChatTalkerFromApi', {
      raw: talker,
      normalized: 'client',
    });
    return 'client';
  }
  if (t === 'bot') {
    debugLog('normalizeChatTalkerFromApi', { raw: talker, normalized: 'bot' });
    return 'bot';
  }
  if (t === 'agent') {
    debugLog('normalizeChatTalkerFromApi', {
      raw: talker,
      normalized: 'agent',
    });
    return 'agent';
  }
  if (t === 'summary') {
    debugLog('normalizeChatTalkerFromApi', {
      raw: talker,
      normalized: 'summary',
    });
    return 'summary';
  }
  debugLog('normalizeChatTalkerFromApi', {
    raw: talker,
    normalized: undefined,
  });
  return undefined;
}

/** Normalize GET /chat/init rows (API may send customer | agent). */
export function normalizeChatInitMessages(
  messages: CallMessage[]
): CallMessage[] {
  const normalized = messages.map(m => ({
    ...m,
    talker:
      normalizeChatTalkerFromApi(m.talker as unknown as string) ?? m.talker,
  })) as CallMessage[];
  debugLog('normalizeChatInitMessages', {
    count: messages.length,
    raw: messages.map(summarizeChatMessage),
    normalized: normalized.map(summarizeChatMessage),
  });
  return normalized;
}

type ConnectChatPart = {
  Content?: string;
  ParticipantRole?: string;
  AbsoluteTime?: string;
  Id?: string;
  Type?: string;
  DisplayName?: string;
};

const CONNECT_HISTORY_REPLAY_LINE =
  /^\d{4}\/\d{2}\/\d{2},\s\d{2}:\d{2}:\d{2}\s-\s\([^)]+\)\s:/;

function isConnectHistoryReplayContent(content: string): boolean {
  return content
    .trim()
    .split(/\r?\n/)
    .some(line => CONNECT_HISTORY_REPLAY_LINE.test(line.trim()));
}

/** Fallback identity for live Connect rows (no speakerObject). */
export type ChatMessageDisplayContext = {
  clientName?: string | null;
  agentName?: string | null;
};

const trimToUndefined = (value?: string | null): string | undefined => {
  const trimmed = value?.trim();
  const normalized = trimmed ? trimmed : undefined;
  debugLog('trimToUndefined', { raw: value, normalized });
  return normalized;
};

function talkerToSpeakerType(
  talker: CallMessage['talker']
): TranscriptSpeakerType {
  const speakerType =
    talker === 'summary'
      ? 'summary'
      : talker === 'bot'
        ? 'bot'
        : talker === 'agent'
          ? 'agent'
          : 'customer';
  debugLog('talkerToSpeakerType', { talker, speakerType });
  return speakerType;
}

/** Init rows use speakerObject.type; live Connect rows fall back to talker. */
export function resolveChatMessageSpeakerType(
  message: CallMessage
): TranscriptSpeakerType {
  if (message.speakerObject?.type) {
    debugLog('resolveChatMessageSpeakerType', {
      source: 'speakerObject',
      speakerType: message.speakerObject.type,
      message: summarizeChatMessage(message),
    });
    return message.speakerObject.type;
  }
  const speakerType = talkerToSpeakerType(message.talker);
  debugLog('resolveChatMessageSpeakerType', {
    source: 'talker',
    speakerType,
    message: summarizeChatMessage(message),
  });
  return speakerType;
}

function resolveNameFromChatSpeakerObject(
  speakerType: TranscriptSpeakerType,
  speakerObject?: ChatMessageSpeakerDto
): string | undefined {
  if (!speakerObject) {
    debugLog('resolveNameFromChatSpeakerObject', {
      speakerType,
      speakerObject,
      speakerName: undefined,
    });
    return undefined;
  }
  // Bot/summary use fixed UI labels (BoostAI / AI Summary), not userName.
  if (speakerType === 'bot' || speakerType === 'summary') {
    debugLog('resolveNameFromChatSpeakerObject', {
      speakerType,
      speakerObject,
      speakerName: undefined,
    });
    return undefined;
  }
  const speakerName = trimToUndefined(speakerObject.userName);
  debugLog('resolveNameFromChatSpeakerObject', {
    speakerType,
    speakerObject,
    speakerName,
  });
  return speakerName;
}

export function resolveChatSpeakerName(
  speakerType: TranscriptSpeakerType,
  context: ChatMessageDisplayContext,
  speakerObject?: ChatMessageSpeakerDto
): string | undefined {
  if (speakerType === 'bot' || speakerType === 'summary') {
    debugLog('resolveChatSpeakerName', {
      source: 'fixed-label',
      speakerType,
      context,
      speakerObject,
      speakerName: undefined,
    });
    return undefined;
  }

  const fromObject = resolveNameFromChatSpeakerObject(
    speakerType,
    speakerObject
  );
  if (fromObject) {
    debugLog('resolveChatSpeakerName', {
      source: 'speakerObject',
      speakerType,
      context,
      speakerObject,
      speakerName: fromObject,
    });
    return fromObject;
  }

  // Live Connect rows only — init rows always include speakerObject.
  if (speakerType === 'customer') {
    const speakerName = trimToUndefined(context.clientName);
    debugLog('resolveChatSpeakerName', {
      source: 'clientName-context',
      speakerType,
      context,
      speakerObject,
      speakerName,
    });
    return speakerName;
  }
  if (speakerType === 'agent' || speakerType === 'agent_2') {
    const speakerName = trimToUndefined(context.agentName);
    debugLog('resolveChatSpeakerName', {
      source: 'agentName-context',
      speakerType,
      context,
      speakerObject,
      speakerName,
    });
    return speakerName;
  }
  debugLog('resolveChatSpeakerName', {
    source: 'unresolved',
    speakerType,
    context,
    speakerObject,
    speakerName: undefined,
  });
  return undefined;
}

/**
 * Map Connect transcript item or onMessage payload → CallMessage.
 * Live events use slightly stricter role rules than transcript polling.
 */
export function connectParticipantToCallMessage(
  part: ConnectChatPart,
  fromLiveEvent: boolean
): CallMessage | null {
  if (!part.Content) {
    debugLog('connectParticipantToCallMessage skipped', {
      reason: 'missing Content',
      part,
      fromLiveEvent,
    });
    return null;
  }
  if (fromLiveEvent && isConnectHistoryReplayContent(part.Content)) {
    debugLog('connectParticipantToCallMessage skipped', {
      reason: 'connect history replay',
      part,
      fromLiveEvent,
    });
    return null;
  }
  const role = (part.ParticipantRole ?? '').toLowerCase();
  const isAgent = fromLiveEvent
    ? role === 'agent' ||
      role === 'system' ||
      (part.Type !== 'MESSAGE' && part.Type !== 'EVENT'
        ? false
        : role !== 'customer')
    : role === 'agent' || role === 'system';
  const message: CallMessage = {
    talker: isAgent ? 'agent' : 'client',
    message: part.Content,
    timestamp: part.AbsoluteTime ?? undefined,
    messageId: part.Id,
  };
  debugLog('connectParticipantToCallMessage', {
    part,
    fromLiveEvent,
    role,
    isAgent,
    message: summarizeChatMessage(message),
  });
  return message;
}

export interface ReplayDedupState {
  /** Next index in the init-history reference to match against. */
  cursor: number;
  /** True once a live message stops matching the reference — everything after is new. */
  done: boolean;
}

function findForwardIndex(
  reference: CallMessage[],
  from: number,
  predicate: (message: CallMessage) => boolean
): number {
  for (let i = from; i < reference.length; i += 1) {
    if (predicate(reference[i])) return i;
  }
  return -1;
}

/**
 * Connect replay and init history share body text but different ids/timestamps.
 * Compare a replayed row to a live row: human talkers must agree, but a replayed
 * non-human row (bot/summary) arrives as 'agent' on the live Connect side.
 */
function talkersCompatible(
  initTalker: CallMessage['talker'],
  liveTalker: CallMessage['talker']
): boolean {
  if (initTalker === liveTalker) return true;
  if (
    (initTalker === 'bot' || initTalker === 'summary') &&
    liveTalker === 'agent'
  ) {
    return true;
  }
  return false;
}

/**
 * Order-based replay dedup. The Connect accept replay is a contiguous prefix of
 * init history, so we match forward from a monotonic cursor instead of scanning
 * the whole history per message. A repeated body ("ok") no longer matches an
 * already-consumed row.
 */
export function classifyReplayMessage(
  msg: CallMessage,
  reference: CallMessage[],
  state: ReplayDedupState
): { duplicate: boolean; next: ReplayDedupState } {
  if (state.done) {
    return { duplicate: false, next: state };
  }

  // 1. Exact id, forward from cursor (no substring matching).
  if (msg.messageId) {
    const idx = findForwardIndex(
      reference,
      state.cursor,
      m => m.messageId === msg.messageId
    );
    if (idx >= 0) {
      debugLog('classifyReplayMessage matched', {
        reason: 'messageId',
        incoming: summarizeChatMessage(msg),
        reference: summarizeChatMessage(reference[idx]),
        cursor: idx + 1,
      });
      return { duplicate: true, next: { cursor: idx + 1, done: false } };
    }
  }

  // 2. Positional text + talker match, forward from cursor.
  const text = normalizeMessageText(msg.message);
  if (text) {
    const idx = findForwardIndex(
      reference,
      state.cursor,
      m =>
        normalizeMessageText(m.message) === text &&
        talkersCompatible(m.talker, msg.talker)
    );
    if (idx >= 0) {
      debugLog('classifyReplayMessage matched', {
        reason: 'message text',
        incoming: summarizeChatMessage(msg),
        reference: summarizeChatMessage(reference[idx]),
        cursor: idx + 1,
      });
      return { duplicate: true, next: { cursor: idx + 1, done: false } };
    }
  }

  debugLog('classifyReplayMessage not matched', {
    reason: 'no forward match — replay done',
    incoming: summarizeChatMessage(msg),
    cursor: state.cursor,
  });
  return { duplicate: false, next: { cursor: state.cursor, done: true } };
}

const CHAT_NOTE_SEGMENT_PREFIX = 'chat-msg:';

function pushChatSegment(
  segments: TranscriptSegmentDisplay[],
  segment: Omit<TranscriptSegmentDisplay, 'speakerName'> & {
    speakerType: TranscriptSpeakerType;
    speakerObject?: ChatMessageSpeakerDto;
  },
  context: ChatMessageDisplayContext
): void {
  const { speakerObject, ...display } = segment;
  const displaySegment = {
    ...display,
    speakerName: resolveChatSpeakerName(
      segment.speakerType,
      context,
      speakerObject
    ),
  };
  debugLog('pushChatSegment', { segment, context, displaySegment });
  segments.push(displaySegment);
}

/**
 * Build transcript segments for chat UI.
 * Preserves backend message order; does not re-sort.
 */
export function mapChatMessagesToDisplaySegments(
  messages: CallMessage[],
  segmentNotes: Record<string, TranscriptSegmentNote[]>,
  context: ChatMessageDisplayContext = {}
): TranscriptSegmentDisplay[] {
  debugLog('mapChatMessagesToDisplaySegments input', {
    count: messages.length,
    messages: messages.map(summarizeChatMessage),
    segmentNoteKeys: Object.keys(segmentNotes),
    context,
  });
  const segments: TranscriptSegmentDisplay[] = [];

  messages.forEach((m, i) => {
    const speakerType = resolveChatMessageSpeakerType(m);
    const baseId = m.messageId ?? `chat:${i}`;
    const apiNotes = m.messageId
      ? (segmentNotes[`${CHAT_NOTE_SEGMENT_PREFIX}${m.messageId}`] ?? [])
      : [];
    const notes = [...(m.notes ?? []), ...apiNotes];

    pushChatSegment(
      segments,
      {
        id: baseId,
        text: m.message ?? '',
        speaker: speakerType,
        speakerType,
        isPartial: false,
        timestamp: m.timestamp,
        notes,
        speakerObject: m.speakerObject,
        notePayload: m.messageId
          ? { type: 'chat' as const, messageId: m.messageId }
          : undefined,
      },
      context
    );
  });

  debugLog('mapChatMessagesToDisplaySegments output', {
    count: segments.length,
    segments,
  });
  return segments;
}
